import { RefObject, useEffect } from 'react';

type TUseArrowKeyCycleFocusOptions = {
  containerRef?: RefObject<HTMLElement | null> | null;
  onTab?: (event: KeyboardEvent) => void;
  onUp?: (event: KeyboardEvent) => void;
  onDown?: (event: KeyboardEvent) => void;
  onEnter?: (event: KeyboardEvent) => void;
  reRenderCounter?: number;
};

const useArrowKeyCycleFocus = ({
  containerRef,
  onDown,
  onTab,
  onUp,
  onEnter,
  reRenderCounter,
}: TUseArrowKeyCycleFocusOptions) => {
  useEffect(() => {
    const contentEl = containerRef?.current;
    if (!contentEl) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      handleArrowCycle({ event, contentEl, onTab, onDown, onUp, onEnter });
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, onDown, onEnter, onTab, onUp, reRenderCounter]);
};

export default useArrowKeyCycleFocus;

export const handleArrowCycle = ({
  event,
  contentEl,
  onTab,
  onDown,
  onUp,
  onEnter,
  elementTags = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]',
}: {
  event: KeyboardEvent;
  contentEl: HTMLElement;
  onTab?: (event: KeyboardEvent) => void;
  onDown?: (event: KeyboardEvent) => void;
  onUp?: (event: KeyboardEvent) => void;
  onEnter?: (event: KeyboardEvent) => void;
  elementTags?: string;
}) => {
  if (event.key === 'Tab') {
    // close the popover
    onTab?.(event);
  }
  if (event.key === 'Enter') {
    onEnter?.(event);
    return;
  }

  if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
  event.preventDefault();

  const focusableElements = contentEl.querySelectorAll(elementTags);
  const firstFocusableElement = (focusableElements?.[0] || null) as HTMLElement | null;
  const lastFocusableElement = (focusableElements?.[focusableElements.length - 1] || null) as HTMLElement | null;

  if (!firstFocusableElement || !lastFocusableElement) return;

  // if open and display is not none,
  // get all tabbable elements and cycle through them when the up/down arrow keys are pressed
  // make sure to cycle back to the first element when the last element is reached
  // and vice versa
  if (event.key === 'ArrowDown') {
    onDown?.(event);
    if (document.activeElement === lastFocusableElement) {
      firstFocusableElement.focus();
    } else {
      let nextElement;
      if (document.activeElement) {
        nextElement = focusableElements[Array.from(focusableElements).indexOf(document.activeElement) + 1];
      } else {
        firstFocusableElement.focus();
      }
      if (nextElement) {
        (nextElement as HTMLElement).focus();
      }
    }
  } else if (event.key === 'ArrowUp') {
    onUp?.(event);
    if (document.activeElement === firstFocusableElement) {
      lastFocusableElement?.focus();
    } else {
      let prevElement;
      const isActiveElementChildOfContainer = contentEl.contains(document.activeElement);
      if (document.activeElement && isActiveElementChildOfContainer) {
        prevElement = focusableElements[Array.from(focusableElements).indexOf(document.activeElement) - 1];
      } else {
        lastFocusableElement?.focus();
      }
      if (prevElement) {
        (prevElement as HTMLElement).focus();
      }
    }
  }
};
